import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/docs/plugins/getting-started")({
	component: GettingStartedPage,
});

function H({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<h2
			id={id}
			className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mt-14 mb-4 pt-6 border-t border-zinc-100 dark:border-zinc-800/50 scroll-mt-20 first:border-0 first:mt-0 first:pt-0"
		>
			{children}
		</h2>
	);
}

function Pre({ title, children }: { title?: string; children: string }) {
	return (
		<div className="rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden mb-4">
			{title && (
				<div className="px-3 py-1 text-[9px] text-zinc-500 uppercase tracking-widest border-b border-zinc-800 font-medium">
					{title}
				</div>
			)}
			<pre className="px-3 py-2 overflow-x-auto text-[11px] leading-relaxed text-zinc-300 font-mono">
				{children}
			</pre>
		</div>
	);
}

function GettingStartedPage() {
	const { t } = useTranslation("docs");
	return (
		<div className="w-full">
			<div className="mb-4">
				<h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
					{t("getting_started_title")}
				</h1>
				<p className="mt-1.5 text-[13px] text-zinc-500">
					{t("getting_started_intro")}
				</p>
			</div>

			<H id="requirements">{t("getting_started_requirements")}</H>
			<ul className="text-[12px] text-zinc-500 space-y-1.5 list-disc list-inside mb-4">
				<li>
					{t("getting_started_req_rust")}{" "}
					<code className="text-[11px] font-mono">wasm32-wasip1</code>
				</li>
				<li>
					CLI: <code className="text-[11px] font-mono">omnios</code> (
					{t("getting_started_req_cli")})
				</li>
			</ul>
			<Pre title="shell">{"rustup target add wasm32-wasip1"}</Pre>

			<H id="scaffold">{t("getting_started_cli_scaffold")}</H>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("getting_started_cli_scaffold_desc")}
			</p>
			<Pre title="shell">{`# Создаёт plugins/my-plugin/ с Cargo.toml, plugin.toml и src/lib.rs
omnios plugins init my-plugin

# Или в произвольную директорию
omnios plugins init my-plugin --dir ./path/to/plugin`}</Pre>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("getting_started_cli_manual_note")}
			</p>

			<H id="cargo-toml">{t("getting_started_cargo_toml")}</H>
			<Pre title="Cargo.toml">{`[package]
name = "my-plugin"
version = "0.1.0"
edition = "2021"

[lib]
crate-type = ["cdylib"]

[dependencies]
plugin-sdk = { path = "../../backend/crates/plugin-sdk" }
plugin-abi = { path = "../../backend/crates/plugin-abi" }

[profile.release]
opt-level = "s"
lto = true`}</Pre>

			<H id="manifest">{t("getting_started_manifest")}</H>
			<Pre title="plugin.toml">{`[plugin]
id = "com.example.my-plugin"
name = "My Plugin"
version = "0.1.0"
description = "Извлекает бренд из названия"
author = "Your Name"

[capabilities]
db_read = ["product"]
kv = true
max_memory_bytes = 67108864
max_execution_ms = 10000

[[extensions]]
point = "enrichment.extract"
handler = "extract_brand"
priority = 50`}</Pre>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("getting_started_manifest_desc") }}
			/>

			<H id="code">{t("getting_started_code")}</H>
			<Pre title="src/lib.rs">{`// src/lib.rs
use plugin_sdk::prelude::*;

extraction_handler!(extract_brand, |input: EnrichmentInput| {
    let title = &input.title;
    let brand = title.split_whitespace().next().unwrap_or("").to_string();

    // Кэшируем в KV
    let cache_key = format!("brand:{}", input.product_id);
    let _ = kv_set(&cache_key, json!({"brand": &brand}));

    log_info(&format!("Extracted brand '{}' from '{}'", brand, title));

    ExtractResult {
        confidence: if brand.is_empty() { 0.0 } else { 0.8 },
        action: "extracted".into(),
        extracted: json!({ "brand": {"value": brand} }),
        metadata: None,
    }
});`}</Pre>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("getting_started_code_desc") }}
			/>

			<H id="build">{t("getting_started_build")}</H>
			<Pre title="shell">{`# Через CLI (рекомендуется)
omnios plugins build

# Или с флагом release
omnios plugins build --release

# Вручную
cargo build --target wasm32-wasip1 --release
# → target/wasm32-wasip1/release/my_plugin.wasm`}</Pre>

			<H id="publish">{t("getting_started_publish")}</H>
			<Pre title="shell">{`# Сборка + публикация одной командой
omnios plugins dev

# Или по отдельности
omnios plugins build --release
omnios plugins publish`}</Pre>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				<span
					dangerouslySetInnerHTML={{ __html: t("getting_started_upload_note") }}
				/>{" "}
				<Link to="/settings/plugins" className="text-primary hover:underline">
					{t("getting_started_settings_plugins")}
				</Link>
				.
			</p>

			<div className="mt-14 pt-4 border-t border-zinc-100 dark:border-zinc-800/50">
				<p className="text-[13px] text-zinc-500">
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("getting_started_next")}
					</strong>{" "}
					<Link
						to="/docs/plugins/sdk-reference"
						className="text-primary hover:underline"
					>
						{t("sdk_reference")}
					</Link>{" "}
					— {t("getting_started_next_sdk")}
				</p>
			</div>
		</div>
	);
}
